import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  AppSvgIconComponent,
  OverlayService,
  SignalButton,
  SignalCheckbox,
  SignalDropdown,
  SignalRadio,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { ConfirmOverlayComponent } from '@core/components/confirm-overlay/confirm-overlay';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionDataResponse } from '@core/api/workspace/models/dataLibraryCollectionDataResponse';
import type { DataLibraryCollectionFieldResponse } from '@core/api/workspace/models/dataLibraryCollectionFieldResponse';
import type { PatchDataLibraryCollectionApiResponse } from '@core/api/workspace/models/patchDataLibraryCollectionApiResponse';
import { PatchDataLibraryCollectionFieldRequestValueType } from '@core/api/workspace/models/patchDataLibraryCollectionFieldRequestValueType';
import type {
  CollectionFieldValueType,
  ColumnDraft,
} from '../create-collection-page/create-collection-page';
import { createColumnDraft } from '../create-collection-page/create-collection-page';
import { dataLibraryTypeBadgeClass } from '../data-library-type-styles';

export interface EditCollectionColumnsOverlayData {
  collectionId: string;
  name: string;
  fields: DataLibraryCollectionFieldResponse[];
  uniqueFieldKey: string;
}

export type EditCollectionColumnsOverlayResult = DataLibraryCollectionDataResponse;

const VALUE_TYPE_OPTIONS: { label: string; value: CollectionFieldValueType }[] =
  [
    { label: 'Text', value: 'text' },
    { label: 'Number', value: 'number' },
    { label: 'Boolean', value: 'boolean' },
    { label: 'Date', value: 'date' },
  ];

const VALUE_TYPE_ICONS: Record<CollectionFieldValueType, string> = {
  text: 'assets/icons/text-string.svg',
  number: 'assets/icons/numeric.svg',
  date: 'assets/icons/calendar-blank-outline.svg',
  boolean: 'assets/icons/toggle-switch-outline.svg',
};

const VALUE_TYPE_API: Record<
  CollectionFieldValueType,
  PatchDataLibraryCollectionFieldRequestValueType
> = {
  text: PatchDataLibraryCollectionFieldRequestValueType.text,
  number: PatchDataLibraryCollectionFieldRequestValueType.number,
  boolean: PatchDataLibraryCollectionFieldRequestValueType.boolean,
  date: PatchDataLibraryCollectionFieldRequestValueType.date,
};

interface EditColumnDraft extends ColumnDraft {
  key?: string;
}

@Component({
  selector: 'app-edit-collection-columns-overlay',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    SignalButton,
    SignalCheckbox,
    SignalDropdown,
    SignalRadio,
    SignalTextField,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
  ],
  templateUrl: './edit-collection-columns-overlay.html',
  styleUrl: './edit-collection-columns-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditCollectionColumnsOverlayComponent extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);
  private readonly dialogRef = inject(
    DialogRef<EditCollectionColumnsOverlayResult | null>,
  );
  private readonly data = inject<EditCollectionColumnsOverlayData>(DIALOG_DATA);

  readonly valueTypeOptions = VALUE_TYPE_OPTIONS;
  readonly saveState = new State<PatchDataLibraryCollectionApiResponse>();

  readonly name = signal(this.data.name);
  readonly columns = signal<EditColumnDraft[]>(
    this.data.fields.map((field) =>
      createColumnDraft({
        label: field.name,
        valueType: field.valueType as CollectionFieldValueType,
        required: field.required !== false,
      }),
    ).map((draft, index) => ({
      ...draft,
      key: this.data.fields[index].key,
    })),
  );
  readonly uniqueColumnId = signal(
    this.columns().find((col) => col.key === this.data.uniqueFieldKey)?.id ??
      this.columns()[0].id,
  );

  close(): void {
    this.dialogRef.close(null);
  }

  addColumn(): void {
    const draft = createColumnDraft();
    this.columns.update((cols) => [...cols, draft]);
    this.scrollToColumn(draft.id);
  }

  removeColumn(id: number): void {
    const cols = this.columns();
    if (cols.length <= 1) {
      return;
    }

    const column = cols.find((col) => col.id === id);
    if (!column) {
      return;
    }

    // Newly added columns (no persisted key) can be removed without confirm.
    if (!column.key) {
      this.applyColumnRemoval(id);
      return;
    }

    const label = column.label.trim() || column.key;
    const confirmRef = this.overlayService.openModal(ConfirmOverlayComponent, {
      data: {
        title: 'Delete column',
        message: `Delete “${label}”? Existing row values for this column will be permanently removed when you save. This cannot be undone.`,
        confirmLabel: 'Delete',
      },
      panelClass: ['confirm-overlay-panel'],
      fitContent: true,
    });

    confirmRef.closed.subscribe((confirmed: boolean | undefined) => {
      if (!confirmed) {
        return;
      }
      this.applyColumnRemoval(id);
    });
  }

  private applyColumnRemoval(id: number): void {
    const cols = this.columns();
    if (cols.length <= 1) {
      return;
    }
    const next = cols.filter((col) => col.id !== id);
    this.columns.set(next);
    if (this.uniqueColumnId() === id) {
      this.uniqueColumnId.set(next[0].id);
    }
  }

  private scrollToColumn(id: number): void {
    setTimeout(() => {
      const el = document.querySelector(
        `[data-column-id="${id}"]`,
      ) as HTMLElement | null;
      if (!el) {
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const input = el.querySelector('input') as HTMLInputElement | null;
      input?.focus({ preventScroll: true });
    });
  }

  setLabel(id: number, label: string): void {
    this.patchColumn(id, { label });
  }

  setValueType(id: number, value: unknown): void {
    this.patchColumn(id, { valueType: value as CollectionFieldValueType });
  }

  setUnique(id: unknown): void {
    if (typeof id !== 'number') {
      return;
    }
    this.uniqueColumnId.set(id);
  }

  setRequired(id: number, required: boolean): void {
    this.patchColumn(id, { required });
  }

  iconSrcForType(valueType: CollectionFieldValueType): string {
    return VALUE_TYPE_ICONS[valueType];
  }

  typeChipClass(valueType: CollectionFieldValueType): string {
    return dataLibraryTypeBadgeClass(valueType);
  }

  onColumnDrop(event: CdkDragDrop<EditColumnDraft[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const cols = [...this.columns()];
    moveItemInArray(cols, event.previousIndex, event.currentIndex);
    this.columns.set(cols);
  }

  save(): void {
    const name = this.name().trim();
    if (!name) {
      this.toasterService.error('Enter a name.');
      return;
    }

    const columns = this.columns();
    for (const column of columns) {
      if (!column.label.trim()) {
        this.toasterService.error('Enter a label for every column.');
        return;
      }
    }

    const uniqueColumn = columns.find(
      (column) => column.id === this.uniqueColumnId(),
    );
    if (!uniqueColumn) {
      this.toasterService.error('Select a unique column.');
      return;
    }
    if (
      uniqueColumn.valueType !== 'text' &&
      uniqueColumn.valueType !== 'number'
    ) {
      this.toasterService.error('The unique column must be Text or Number.');
      return;
    }
    if (!columns.some((column) => column.valueType === 'text')) {
      this.toasterService.error('Add at least one Text column.');
      return;
    }

    // Temporary uniqueFieldKey: prefer preserved key, else label-derived will be assigned server-side.
    // Send fields first without unique key matching new columns — server needs uniqueFieldKey matching
    // a field.key after resolution. For new columns without key, we pass unique by preferring existing key
    // or the keyify of the unique column label won't match until server generates.
    // Strategy: always include key when present; for new columns omit key. uniqueFieldKey must be the
    // unique column's existing key OR we set uniqueFieldKey to a placeholder.
    // resolveCollectionFieldsByUniqueKey finds by preferred key on input first.
    // If unique column is new (no key), findIndex by key fails — then keys are generated and we look up.
    // For new unique column without key, uniqueFieldKey string won't match generated key unless we
    // pass uniqueFieldIndex. Patch API uses uniqueFieldKey only.
    // Fix: ensure unique column always has a key — if new, pre-assign a temporary key client-side? Or
    // change API. Easiest: if unique has no key, use first generated approach by sending uniqueFieldKey
    // as the result of a local keyify matching buildUniqueFieldKeys order.

    const fieldsPayload = columns.map((column) => ({
      ...(column.key ? { key: column.key } : {}),
      name: column.label.trim(),
      valueType: VALUE_TYPE_API[column.valueType],
      required: column.required,
    }));

    let uniqueFieldKey = uniqueColumn.key;
    if (!uniqueFieldKey) {
      // Mirror server key generation order for the unique column index.
      uniqueFieldKey = this.previewUniqueKey(fieldsPayload, columns.indexOf(uniqueColumn));
    }

    this.executeRequest({
      state: this.saveState,
      request: this.userAPIService.patchDataLibraryCollection(
        this.data.collectionId,
        {
          name,
          fields: fieldsPayload,
          uniqueFieldKey,
        },
      ),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        this.toasterService.success('Columns updated.');
        this.dialogRef.close(response.data);
      },
    });
  }

  private previewUniqueKey(
    fields: Array<{ name: string; key?: string }>,
    uniqueIndex: number,
  ): string {
    // Lightweight mirror of server keyify for predicting the unique field key.
    const used = new Set<string>();
    const keys = fields.map((field) => {
      const base = this.keyify(field.key || field.name);
      let candidate = base;
      let suffix = 1;
      while (used.has(candidate)) {
        suffix += 1;
        candidate = `${base.slice(0, Math.max(1, 64 - String(suffix).length))}${suffix}`;
      }
      used.add(candidate);
      return candidate;
    });
    return keys[uniqueIndex];
  }

  private keyify(value: string): string {
    const segments = value
      .trim()
      .split(/[^a-zA-Z0-9]+/)
      .map((segment) => segment.replace(/[^a-zA-Z0-9]/g, ''))
      .filter((segment) => segment.length > 0);
    if (segments.length === 0) {
      return 'field';
    }
    let key =
      segments[0].toLowerCase() +
      segments
        .slice(1)
        .map(
          (segment) =>
            segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase(),
        )
        .join('');
    if (!/^[a-z]/.test(key)) {
      key = `v${key}`;
    }
    return key.slice(0, 64);
  }

  private patchColumn(id: number, patch: Partial<EditColumnDraft>): void {
    this.columns.update((cols) =>
      cols.map((col) => (col.id === id ? { ...col, ...patch } : col)),
    );
  }
}
